import React, { useState, useEffect } from 'react';
import { Card, Metric, Text, Flex, Badge } from "@tremor/react";
import { getUsage } from "../services/user";

/**
 * Calculate device statistics
 */
function calculateDeviceStats(devices) {
  const total = devices.length;
  let online = 0;
  let recent = 0;
  let offline = 0;
  let totalCapabilities = 0;
  let devicesWithData = 0;
  
  devices.forEach(device => {
    // Count capabilities
    if (device.capabilities && device.capabilities.length > 0) {
      totalCapabilities += device.capabilities.length;
      devicesWithData++;
    }
    
    // Determine status based on device type
    if (!device.lastOnline) {
      offline++;
      return;
    }
    
    const now = new Date();
    const lastSeen = new Date(device.lastOnline);
    const minutesSince = (now - lastSeen) / (1000 * 60);
    
    if (device.type === 'lora') {
      // LoRa devices: Online < 60min, Offline >= 60min
      if (minutesSince < 60) {
        online++;
      } else {
        offline++;
      }
    } else {
      // MQTT devices: Online < 5min, Recent < 60min, Offline >= 60min
      if (minutesSince < 5) {
        online++;
      } else if (minutesSince < 60) {
        recent++;
      } else {
        offline++;
      }
    }
  });
  
  return {
    total,
    online,
    recent,
    offline,
    totalCapabilities,
    devicesWithData,
    avgCapabilitiesPerDevice: devicesWithData > 0 ? (totalCapabilities / devicesWithData).toFixed(1) : 0
  };
}

/**
 * Determine the severity level for the status card based on fleet health.
 * "recent" devices count as online for threshold calculation.
 */
function getStatusSeverity(stats) {
  if (stats.total === 0) return 'neutral';
  const onlinePercent = ((stats.online + stats.recent) / stats.total) * 100;
  if (onlinePercent === 100) return 'neutral';
  if (onlinePercent > 50) return 'healthy';
  if (onlinePercent >= 25) return 'warning';
  return 'critical';
}

const severityStyles = {
  neutral: {
    card: '',
    border: '',
    label: 'text-tremor-content dark:text-dark-tremor-content',
  },
  healthy: {
    card: 'bg-green-50/50 dark:bg-green-950/40',
    border: 'border border-green-200',
    label: 'text-green-700 dark:text-green-300',
  },
  warning: {
    card: 'bg-amber-50/50 dark:bg-amber-950/40',
    border: 'border border-amber-200',
    label: 'text-amber-700 dark:text-amber-300',
  },
  critical: {
    card: 'bg-rose-50/50 dark:bg-rose-950/40',
    border: 'border border-rose-200',
    label: 'text-rose-600 dark:text-rose-300',
  },
};

export default function DeviceStatsCards({ devices }) {
  const [totalEvents, setTotalEvents] = useState(null);
  const stats = calculateDeviceStats(devices);
  const severity = getStatusSeverity(stats);
  
  useEffect(() => {
    const fetchTotalEvents = async () => {
      try {
        const usage = await getUsage();
        
        // Get today's date in MM/DD format to match the API response
        const today = new Date();
        const todayString = `${String(today.getMonth() + 1).padStart(2, '0')}/${String(today.getDate()).padStart(2, '0')}`;
        
        // Find today's data in the usage array
        const todayData = usage.data?.find(day => day._id === todayString);
        const todayEvents = todayData?.Uplinks || 0;
        
        setTotalEvents(todayEvents);
      } catch (error) {
        console.error('Error fetching usage data:', error);
        setTotalEvents(0);
      }
    };
    
    if (devices.length > 0) {
      fetchTotalEvents();
    }
  }, [devices]);
  
  if (devices.length === 0) {
    return null;
  }
  
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      {/* Total Devices */}
      <Card className="p-4">
        <Flex alignItems="start">
          <div className="truncate">
            <Text>Total Devices</Text>
            <Metric className="truncate">{stats.total}</Metric>
          </div>
        </Flex>
      </Card>
      
      {/* Online Status */}
      <Card className={`p-4 ${severityStyles[severity].card} ${severityStyles[severity].border}`}>
        <Flex alignItems="start">
          <div className="truncate">
            <Text className={severityStyles[severity].label}>Device Status</Text>
            {severity === 'critical' ? (
              <>
                <Metric className="truncate text-rose-600">{stats.offline} <span className="text-sm font-normal">Offline</span></Metric>
                <div className="flex gap-1 mt-1">
                  <Badge color="green" size="xs">{stats.online} Online</Badge>
                  <Badge color="yellow" size="xs">{stats.recent} Recent</Badge>
                </div>
              </>
            ) : (
              <div className="flex gap-1 mt-1">
                <Badge color="green" size="xs">{stats.online} Online</Badge>
                <Badge color="yellow" size="xs">{stats.recent} Recent</Badge>
                <Badge color="red" size="xs">{stats.offline} Offline</Badge>
              </div>
            )}
          </div>
        </Flex>
      </Card>
      
      {/* Data Sensors */}
      <Card className="p-4">
        <Flex alignItems="start">
          <div className="truncate">
            <Text>Total Sensors</Text>
            <Metric className="truncate">{stats.totalCapabilities}</Metric>
            <Text className="text-xs text-tremor-content-subtle dark:text-dark-tremor-content-subtle">
              Across {stats.devicesWithData} devices
            </Text>
          </div>
        </Flex>
      </Card>
      
      {/* Total Events */}
      <Card className="p-4">
        <Flex alignItems="start">
          <div className="truncate">
            <Text>Total Events</Text>
            <Metric className="truncate">
              {totalEvents !== null ? totalEvents.toLocaleString() : '--'}
            </Metric>
            <Text className="text-xs text-tremor-content-subtle dark:text-dark-tremor-content-subtle">
              Events today
            </Text>
          </div>
        </Flex>
      </Card>
    </div>
  );
}